import React, { useState, useEffect } from "react";
import { Loader } from "../Loader";

interface KpiReportLoaderProps<T> {
  load: () => Promise<T>;
  children: (data: T) => React.ReactNode;
}

// Fetches a KPI report once on mount and owns the loading, error (with Retry)
// and empty states, so each KPI page only supplies how to load and how to render.
export function KpiReportLoader<T>({ load, children }: KpiReportLoaderProps<T>) {
  const [kpiData, setKpiData] = useState<T | null>(null);
  const [, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchKpiReport();
    // Fetch once on mount; Retry is the only other trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchKpiReport = async () => {
    try {
      setLoading(true);
      const response = await load();
      setKpiData(response);
      setError(null);
    } catch (err) {
      setError("Failed to fetch KPI report");
      console.error("Error fetching KPI report:", err);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  // Show full page loader only on initial load
  if (initialLoad) {
    return (
      <div className="loading-page">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="error mb-4">
        {error}
        <button
          onClick={fetchKpiReport}
          className="ml-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!kpiData) {
    return <div>No KPI data available</div>;
  }

  return <>{children(kpiData)}</>;
}
